import type {
  ContentPlayEvent,
  ContentPlayKind,
} from "@shared/types/contentPlay";
import { flushEngagement, trackContentPlay } from "./engagement";

/**
 * What actually played on this television, measured (`ANALYTICS-2`).
 *
 * ## The device is the only witness
 *
 * The server hands out a play URL and never hears again. That is why
 * `vodUsage` has no sources, why signage has no proof-of-play, and why nothing
 * in the platform could answer "how many people watched this film". Three
 * candidates could report it and only one works:
 *
 * 1. **CDN / origin logs.** Somebody else's system, no premise identity, no way
 *    to attribute a segment request to a property.
 * 2. **The play-URL mint.** The server DOES know who asked for what, but a mint
 *    is an INTENT: a guest who quits after four seconds mints exactly the same
 *    event as one who watches the film. That is counted, separately and under a
 *    different name - `impressions`, written by `api/screens.ts`. It is never a
 *    play.
 * 3. **The player, reporting progress.** The only witness that can tell
 *    started from watched from completed. This file.
 *
 * ## Idle is not playing, and playing is not healthy
 *
 * The two distinctions the signage surface had to learn on hardware
 * ([[signage-playback-surface]]), and they are the whole design here. A paused
 * player, a stalled player, a pre-warmed spare and a finished player all sit
 * still, and none of them is watch time. So the accrual asks the ONE question
 * that separates them: **did the playhead move, and could it plausibly have
 * moved that far in the wall-clock time since the last sample.**
 *
 * That single rule handles every case without a state machine:
 *
 * - paused, ended, seeking → the playhead does not advance → nothing accrues;
 * - stalled (the decode refusal, the wedged element, the frozen WebView) → the
 *   playhead does not advance → nothing accrues, and no `content_start` is ever
 *   emitted for a title that never rendered a moving picture;
 * - a seek across an hour → wall-clock bounds it to the quarter-second it
 *   actually took;
 * - a sample gap longer than {@link MAX_SAMPLE_SECONDS} → the player stopped
 *   reporting, so that stretch is unattested and is not credited. A television
 *   whose WebView hung for an hour must not come back and bill an hour.
 *
 * ## Heartbeats, not a stream
 *
 * `timeupdate` fires ~4 Hz. A ninety-minute film would be twenty thousand
 * events on a panel whose decoder we already know is fragile
 * ([[panel-video-encoding]]), for a number that only needs to be right to the
 * minute. So watch time accrues locally and is reported once per
 * {@link PROGRESS_HEARTBEAT_SECONDS} of PLAYBACK - which also bounds what a
 * power cut can lose to one heartbeat.
 *
 * ## The end of a session is the case you actually get
 *
 * A guest turning the television off mid-film is the common case, not the edge
 * case. So the tail is flushed on every way out there is: the surface
 * unmounting ({@link PlaySession.end}), and the page going away, where
 * {@link flushOpenPlaySessions} runs before the `sendBeacon` that the
 * engagement channel already fires on `pagehide` / `visibilitychange`.
 *
 * ## What is NOT here
 *
 * No room, no screen id, no session id, no timestamp. The event carries a
 * catalogue id and a number of seconds; the server attributes it to a premise
 * from the screen token and rolls it into a UTC day. See
 * `@shared/types/contentPlay` for the grain and for why `distinctRooms` does
 * not exist.
 */

/** How much PLAYBACK accrues before a heartbeat is emitted. */
export const PROGRESS_HEARTBEAT_SECONDS = 60;

/**
 * The longest gap between two samples that still counts as watched.
 *
 * A progress-reporting player emits ~4 Hz. A gap of half a minute means it
 * stopped reporting - a hung WebView, a suspended timer, a backgrounded page -
 * and whatever happened in that window, this television did not witness it.
 * Crediting it would turn the platform's worst failure mode into its biggest
 * watch-time number.
 */
export const MAX_SAMPLE_SECONDS = 30;

/** What is playing. A catalogue row, never a person and never a place. */
export interface PlaySessionRef {
  kind: ContentPlayKind;
  /** The catalogue id, verbatim - it has to join back to the Content API. */
  assetId: string;
  /** Human title at play time, so a renamed row can still be named. */
  label?: string;
}

/** One reading of the player, as `StreamSurface`'s `onProgress` reports it. */
export interface PlaySample {
  /** The playhead, seconds. */
  currentTime: number;
  paused?: boolean;
  seeking?: boolean;
}

export interface PlaySession {
  /**
   * Feed the player's current state. Cheap and synchronous - safe on a
   * `timeupdate` handler, which is exactly where it is called from.
   */
  sample(state: PlaySample, nowMs?: number): void;
  /**
   * Playback reached the END OF THE ASSET. Not "the guest left": a television
   * switched off mid-film is a session that stops accruing, not a completion.
   */
  complete(): void;
  /** Emit the tail, keep the session open. The unload path. */
  flush(): void;
  /** Emit the tail and close. Calling it twice is a no-op. */
  end(): void;
  /** Watch time accrued but not yet reported, in seconds. For tests. */
  readonly pendingSeconds: number;
  /** Whether the playhead was ever seen to move. For tests. */
  readonly started: boolean;
}

/** Every session currently open, so the unload path can flush all of them. */
const open = new Set<PlaySession>();

let unloadBound = false;

/**
 * Flush every open session, then ship the batch.
 *
 * The engagement channel binds its own `pagehide` / `visibilitychange` beacon,
 * and listener order between the two is not guaranteed - so this ends with an
 * explicit beacon flush of its own rather than assuming it runs first. A second
 * flush of an empty queue costs nothing.
 */
export function flushOpenPlaySessions(): void {
  for (const session of open) session.flush();
  flushEngagement(true);
}

function bindUnload(): void {
  if (unloadBound || typeof document === "undefined") return;
  unloadBound = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushOpenPlaySessions();
  });
  window.addEventListener("pagehide", () => flushOpenPlaySessions());
}

/**
 * Begin measuring one playback.
 *
 * The session is IDLE until the playhead is first seen to move: `content_start`
 * means "a moving picture reached the panel", not "a component mounted". A
 * title that loads and stalls at 0.0s - the decode refusal
 * ([[panel-video-encoding]]) - therefore reports no play at all, which is the
 * truth, and a pre-warmed spare that is never watched reports nothing either.
 *
 * `emit` is injectable so the accrual rules can be tested without a transport.
 */
export function startPlaySession(
  ref: PlaySessionRef,
  nowMs = Date.now(),
  emit: (event: ContentPlayEvent) => void = trackContentPlay
): PlaySession {
  bindUnload();

  const base = {
    kind: ref.kind,
    assetId: ref.assetId,
    ...(ref.label ? { label: ref.label } : {}),
  };

  const startedAt = nowMs;
  let last: { at: number; currentTime: number } | null = null;
  /** Accrued, not yet reported. */
  let pending = 0;
  /** Already reported. `pending + credited` is the session's whole claim. */
  let credited = 0;
  let started = false;
  let closed = false;

  /**
   * The tail, as whole seconds, or 0 when there is nothing worth sending.
   *
   * Sub-second remainders are dropped rather than rounded up: over a long
   * channel session that rounding is a systematic overcount, and this number
   * ends up in a supplier's invoice conversation.
   */
  function take(): number {
    const whole = Math.floor(pending);
    if (whole < 1) return 0;
    pending -= whole;
    credited += whole;
    return whole;
  }

  const session: PlaySession = {
    get pendingSeconds() {
      return pending;
    },
    get started() {
      return started;
    },

    sample(state, at = Date.now()) {
      if (closed) return;
      const previous = last;
      last = { at, currentTime: state.currentTime };
      if (!previous) return; // first reading: a position, not an interval

      const wall = (at - previous.at) / 1000;
      const played = state.currentTime - previous.currentTime;

      // Not playing. The playhead standing still IS the signal - it covers
      // paused, ended, stalled, torn down and muted-spare in one rule - and the
      // transport flags are checked too so a seek's landing sample cannot be
      // read as watch time.
      if (state.paused || state.seeking || played <= 0 || wall <= 0) return;

      /**
       * Credit the SMALLER of "content consumed" and "time elapsed", then bound
       * it by what a reporting player could have witnessed:
       *
       * - a seek forward has a large `played` and a tiny `wall` → the seek is
       *   not watch time;
       * - trick-play at 4x has a large `played` too → wall-clock is the
       *   conservative reading, and conservative is the right direction for a
       *   number somebody is paid on;
       * - a gap this player never reported through is not credited at all.
       */
      if (wall > MAX_SAMPLE_SECONDS) return;
      const observed = Math.min(played, wall);

      /**
       * The session's own wall-clock ceiling. Nothing may claim more watch time
       * than has passed since it began - a client reporting forty thousand
       * seconds in an hour is a bug or an attack, and the first place to refuse
       * it is where the real clock is. The server clamps again on the way in;
       * neither layer trusts the other.
       */
      const room = (at - startedAt) / 1000 - credited - pending;
      const credit = Math.max(0, Math.min(observed, room));
      if (credit <= 0) return;

      pending += credit;

      if (!started) {
        started = true;
        emit({ type: "content_start", ...base });
      }
      if (pending >= PROGRESS_HEARTBEAT_SECONDS) {
        const seconds = take();
        if (seconds) emit({ type: "content_progress", ...base, seconds });
      }
    },

    complete() {
      if (closed) return;
      // A title that never rendered a frame cannot have been completed. The
      // `ended` event of an element that refused to decode is not a viewing.
      if (!started) {
        closed = true;
        open.delete(session);
        return;
      }
      closed = true;
      open.delete(session);
      const seconds = take();
      emit({
        type: "content_complete",
        ...base,
        ...(seconds ? { seconds } : {}),
      });
    },

    flush() {
      if (closed || !started) return;
      const seconds = take();
      if (seconds) emit({ type: "content_progress", ...base, seconds });
    },

    end() {
      if (closed) return;
      session.flush();
      closed = true;
      open.delete(session);
    },
  };

  open.add(session);
  return session;
}

/** Test seam - forget every open session without reporting anything. */
export function resetPlaySessions(): void {
  open.clear();
}
